/**
 * A per-region "working" indicator: an equalizer wave, a label, and a caption.
 *
 * ## Why this exists rather than one loader for the page
 *
 * A screen assembled from several independent reads should show each part as it arrives.
 * Covering everything with one overlay until the slowest read finishes hides data that is
 * already in hand, and tells the user nothing about which part is still coming.
 *
 * ## No percentage, deliberately
 *
 * `features/spi/threeSixty/ComputeProgress` shows one, eased off elapsed time — it is a
 * decay curve, not a measurement, and it never reaches 100%. Real progress would need
 * byte-level download tracking (which gzip makes unreliable) or a count of completed
 * sub-requests, and one region is one request, so a per-region figure could only ever read
 * 0% or 100%. Rather than show a number that means nothing, this shows none.
 *
 * ## The delay is the point of `delayMs`
 *
 * These regions are cached: a warm read returns in single-digit milliseconds. Rendering
 * immediately would flash the animation on every sort, page and size click. Nothing is
 * shown until the wait is long enough to be worth acknowledging, so an instant response
 * simply swaps the rows.
 *
 * ## Accessibility
 *
 * The wave is decorative and hidden from assistive technology; the label and caption carry
 * the meaning and live in a polite live region, so a screen reader announces what is
 * happening once rather than on every animation frame.
 */
import React, { useEffect, useState } from 'react';

import styles from './LoadingWave.module.css';

/** Bars in the wave, and the stagger between them — tuned to read as one wave, not seven. */
const BAR_COUNT = 7;

const BAR_STAGGER_MS = 110;

/**
 * How long a wait must last before it is worth showing.
 *
 * 150ms is the usual floor for "the user noticed": below it the response feels immediate
 * and an indicator is just a flash.
 */
export const LOADING_WAVE_DELAY_MS = 150;

interface LoadingWaveProps {
  /** What is being loaded, e.g. `Loading sales by ASIN…`. */
  readonly label: string;
  /** One line on what is happening, e.g. the column being sorted by. Optional. */
  readonly caption?: string | null;
  /** Overridden only by tests, which cannot wait out a real timer. */
  readonly delayMs?: number;
  readonly testId?: string;
}

export const LoadingWave: React.FC<LoadingWaveProps> = ({
  label, caption, delayMs = LOADING_WAVE_DELAY_MS, testId,
}) => {
  const [visible, setVisible] = useState(delayMs <= 0);

  useEffect(() => {
    if (delayMs <= 0) return undefined;

    const timer = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(timer);
    /*
     * Armed once per MOUNT, deliberately not per label. The owning region renders this
     * only while its request is in flight, so every new wait is a new instance with its
     * own fresh delay. Re-arming on the label instead would make the wave vanish and
     * return mid-wait when a sort follows a search — a flicker in the other direction.
     */
  }, [delayMs]);

  if (!visible) return null;

  return (
    <div
      className="flex flex-col items-center justify-center gap-3 py-8"
      data-testid={testId ?? 'loading-wave'}
    >
      <div className={styles.wrap} aria-hidden="true">
        {Array.from({ length: BAR_COUNT }, (_, index) => (
          <span
            key={index}
            className={styles.bar}
            style={{ animationDelay: `${index * BAR_STAGGER_MS}ms` }}
          />
        ))}
      </div>

      {/*
        `<output>` rather than a span with `role="status"`: it carries that role implicitly and
        is exposed more reliably across assistive technologies (Sonar S6819 — the same reason
        `features/spi/threeSixty/ComputeProgress` uses a real `<progress>`). The behaviour is
        identical: a polite live region, so the label is announced when it appears and again
        when it changes, which is what makes the sort and search messages useful rather than
        decorative.
      */}
      <output className="flex flex-col items-center gap-1">
        <span className="text-sm font-hk-semibold text-[var(--color-text-primary)]">
          {label}
        </span>
        {caption ? (
          <span className="text-xs text-[var(--color-text-muted)]">{caption}</span>
        ) : null}
      </output>
    </div>
  );
};
